import React, { useState } from 'react';
import { Trophy } from 'lucide-react';
import uploadScore from './ScoreUploader';

const ScoreSubmissionDialog = ({
  isOpen,
  onClose,
  score,
  scores,
  onScoreSubmitted,
  className, // Include className in destructured props
}) => {
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const isHighScore = () => {
    if (scores.length < 50) return true;
    return score > scores[49]?.score;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !className.trim()) {
      setError('すべての項目を入力してください');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await uploadScore(name.trim(), score, className.trim());
      onScoreSubmitted();
      onClose();
    } catch (error) {
      setError('スコアの送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isHighScore() || score <= 400) {
    return null;
  }

  return (
    <div className={`fixed inset-0 z-50 ${isOpen ? 'flex' : 'hidden'} items-center justify-center`}>
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-50 w-full max-w-2xl mx-4">
        <div className="bg-gradient-to-b from-slate-900 to-slate-800 border-2 border-blue-400/50 shadow-lg shadow-blue-500/20 backdrop-blur-sm rounded-lg overflow-hidden">
          <div className="p-6 relative">
            {/* Decorative Elements */}
            <div className="absolute -top-1 -left-1 w-3 h-3 bg-blue-400 rounded-full animate-ping opacity-75" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-400 rounded-full animate-ping opacity-75" />
            
            {/* Title and Exit Button */}
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 font-bold tracking-wider">
                🏆 おめでとう！
              </h2>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-400/30 rounded-lg transition-all duration-300 text-lg font-medium"
              >
                終了
              </button>
            </div>
            
            {/* Score Display */}
            <div className="p-6 rounded-lg bg-slate-800/50 border border-blue-500/20 backdrop-blur-sm mb-6 animate-score-fade-in">
              <div className="relative overflow-hidden">
                <p className="text-4xl font-bold text-center text-slate-100 animate-score-bounce">
                  スコア: {" "}
                  <span className="inline-block animate-score-pop text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 
                    transform hover:scale-110 transition-transform duration-300">
                    {score}
                  </span>
                </p>
                <p className="text-xl text-center text-slate-300 mt-4 animate-score-slide">
                  <span className="inline-block animate-pulse">🏆</span> {" "}
                  トップ50に入りました！ {" "}
                  <span className="inline-block animate-pulse">🏆</span>
                </p>
              </div>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="p-6 rounded-lg bg-slate-800/50 border border-blue-500/20 backdrop-blur-sm">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-lg font-medium text-slate-300 mb-2">
                      お名前
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={30}
                      className="w-full px-4 py-3 rounded-lg bg-slate-700/50 border border-blue-500/30 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/50 outline-none text-white text-lg transition-all duration-300"
                      placeholder="お名前を入力してください"
                    />
                  </div>

                  <div>
                    <label htmlFor="class" className="block text-lg font-medium text-slate-300 mb-2">
                      Class
                    </label>
                    <input
                      type="text"
                      id="class"
                      value={className}
                      readOnly
                      className="w-full px-4 py-3 rounded-lg bg-slate-700/50 border border-blue-500/30 text-white text-lg"
                    />
                  </div>

                  {error && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-red-300 text-lg flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />
                      {error}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="
                    px-6 py-3 rounded-lg
                    bg-slate-700/50 hover:bg-slate-700
                    text-slate-300 text-lg font-medium
                    border border-slate-600
                    transition-all duration-300
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="
                    px-8 py-3 rounded-lg
                    bg-gradient-to-r from-blue-500 to-cyan-400
                    hover:from-blue-600 hover:to-cyan-500
                    text-white text-lg font-medium
                    transition-all duration-300
                    hover:shadow-lg hover:shadow-blue-500/20
                    hover:-translate-y-1
                    disabled:opacity-50 disabled:cursor-not-allowed
                    disabled:hover:translate-y-0
                  "
                >
                  {isSubmitting ? '送信中...' : 'スコアを送信'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoreSubmissionDialog;
